import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import { clsx } from 'clsx';
import { Fragment } from 'react';
import { Bar } from 'react-chartjs-2';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router';

import { useGetUser, useGetUserSalesChartData } from '@/api';
import {
    BORDER_RADIUS,
    options,
    placeholderChartData,
    waysToEarnAuth,
    wayToEarnListOne,
    wayToEarnListTwo,
} from '@/components/Drugomaniya/constants.tsx';
import { SalesItem } from '@/components/Drugomaniya/SalesItem/SalesItem.tsx';
import { WayToEarnList } from '@/components/Drugomaniya/WayToEarnList/WayToEarnList.tsx';
import { LinkIcon } from '@/components/icons/drugomaniya';
import { ArrowIcon } from '@/components/icons/main-block';
import { Button } from '@/components/shared/Button';
import { Divider } from '@/components/shared/Divider/Divider.tsx';
import { FAQCollapsible } from '@/components/shared/FAQCollapsible/FAQCollapsible.tsx';
import { LinkWithPrevLocation } from '@/components/shared/LinkWithPrevLocation/LinkWithPrevLocation.tsx';
import { faq } from '@/constants';
import { appRoutes } from '@/routes/app-routes.ts';
import { useAuthStore, useModalStore } from '@/store';
import { getLastSixMonths, getMonth } from '@/utils';

import cn from './styles.module.sass';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

ChartJS.defaults.font.size = 12;
ChartJS.defaults.font.family = 'Montserrat';

export const Drugomaniya = () => {
    const { isAuth, token } = useAuthStore();
    const { setIsWithdrawalOpen, setShowSignUp, setIsOpen, setIsInviteOpen } = useModalStore();

    const { data: userData, isLoading } = useGetUser();
    const { data: operations } = useGetUserSalesChartData();

    const labels = getLastSixMonths();
    const currentMonth = getMonth();
    const nextPassiveIncomeDate = getMonth(true, 1, 15);

    const data = {
        labels,
        datasets: [
            {
                label: 'Мои продажи',
                data: isAuth ? operations?.mySalesSum : placeholderChartData[0],
                backgroundColor: '#3A24C6',
                borderRadius: {
                    topLeft: BORDER_RADIUS,
                    topRight: BORDER_RADIUS,
                    bottomLeft: BORDER_RADIUS,
                    bottomRight: BORDER_RADIUS,
                },
                borderSkipped: false,
                minBarLength: 8,
            },
            {
                label: 'Продажи друзей',
                data: isAuth ? operations?.friendsSalesSum : placeholderChartData[1],
                backgroundColor: '#4D8EEF',
                borderRadius: {
                    topLeft: BORDER_RADIUS,
                    topRight: BORDER_RADIUS,
                    bottomLeft: BORDER_RADIUS,
                    bottomRight: BORDER_RADIUS,
                },
                borderSkipped: false,
                minBarLength: 8,
            },
            {
                label: 'Пассивный доход',
                data: isAuth ? operations?.passiveIncome : placeholderChartData[2],
                backgroundColor: '#B8D4FF',
                borderRadius: {
                    topLeft: BORDER_RADIUS,
                    topRight: BORDER_RADIUS,
                    bottomLeft: BORDER_RADIUS,
                    bottomRight: BORDER_RADIUS,
                },
                borderSkipped: false,
                minBarLength: 8,
            },
            {
                label: 'Нет данных',
                data: isAuth ? operations?.emptyBarData : placeholderChartData[3],
                backgroundColor: '#F3F4F6',
                borderRadius: {
                    topLeft: BORDER_RADIUS,
                    topRight: BORDER_RADIUS,
                    bottomLeft: BORDER_RADIUS,
                    bottomRight: BORDER_RADIUS,
                },
                borderSkipped: false,
                minBarLength: 8,
            },
        ],
    };

    const handleOpenWithdrawal = () => {
        setIsWithdrawalOpen(true);
    };

    const handleOpenSignUp = () => {
        setShowSignUp(true);
        setIsOpen(true);
    };

    const currentMonthData = {
        mySalesSum: operations?.mySalesSum[operations?.mySalesSum.length - 1] ?? 0,
        friendsSalesSum: operations?.friendsSalesSum[operations?.friendsSalesSum.length - 1] ?? 0,
        passiveIncome: operations?.passiveIncome[operations?.passiveIncome.length - 1] ?? 0,
    };

    return (
        <div className={cn.container}>
            <div className={cn.detailsBlock}>
                <div className={cn.balanceCard}>
                    <div className={cn.top}>
                        <h2>Другомания</h2>
                        {!isAuth && !token && <p>Пример кабинета</p>}
                    </div>
                    <div className={cn.bottom}>
                        <div className={cn.balance}>
                            <p className={cn.balanceTitle}>
                                Накоплено {isAuth && '(1 Б = 1 рубль)'}
                            </p>
                            <p className={cn.balanceAmount}>
                                {isAuth &&
                                    (isLoading ? (
                                        <Skeleton width={133} />
                                    ) : (
                                        `${userData?.balance} Б`
                                    ))}
                                {!isAuth && (token ? <Skeleton width={133} /> : '1750 Б')}
                            </p>
                        </div>
                        <div
                            onClick={isAuth ? handleOpenWithdrawal : undefined}
                            className={clsx(cn.rate, isAuth && cn.cursorPointer)}
                        >
                            {isAuth || token ? 'Вывести' : '1 Б = 1 рубль'}
                        </div>
                    </div>
                    <LinkWithPrevLocation to={`/${appRoutes.profile}/${appRoutes.loyaltyProgram}`}>
                        <Button className={cn.waysToEarnBtn}>
                            <div className={cn.coinsIcon}>
                                <img src="/images/icons/coins_gradient.svg" alt="" />
                            </div>
                            <span className={cn.btnText}>2 способа зарабатывать</span>
                            <span className={cn.arrowIcon}>{ArrowIcon}</span>
                        </Button>
                    </LinkWithPrevLocation>
                </div>
                <div className={cn.sales}>
                    <div className={cn.totalEarned}>
                        <div className={cn.totalEarnedTitle}>
                            {isAuth &&
                                (isLoading ? (
                                    <Skeleton width={169} />
                                ) : (
                                    `Заработано за ${currentMonth}`
                                ))}
                            {!isAuth &&
                                (token ? (
                                    <Skeleton width={169} />
                                ) : (
                                    `Заработано за ${currentMonth}`
                                ))}
                        </div>
                        <div className={cn.totalEarnedAmountContainer}>
                            {isLoading ? (
                                <Skeleton width={300} />
                            ) : (
                                <>
                                    <span className={cn.totalEarnedAmount}>
                                        {isAuth
                                            ? currentMonthData.friendsSalesSum +
                                              currentMonthData.mySalesSum
                                            : '1050'}{' '}
                                        Б
                                    </span>
                                    <span className={cn.totalEarnedPrediction}>
                                        +{isAuth ? currentMonthData.passiveIncome : '500'} Б
                                        (пассивный доход - начислим {nextPassiveIncomeDate})
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className={cn.chart}>
                        <Bar height={134} data={data} options={options} />
                    </div>
                    <div className={cn.salesItemsList}>
                        <SalesItem
                            color="#3A24C6"
                            isLoading={isLoading}
                            itemName="Мои продажи"
                            itemDescription={isAuth ? 'В текущем месяце' : undefined}
                            points={isAuth && operations ? currentMonthData.mySalesSum : 350}
                        />
                        <Divider />
                        <SalesItem
                            color="#4D8EEF"
                            isLoading={isLoading}
                            itemName="Продажи друзей"
                            itemDescription={isAuth ? 'В текущем месяце' : undefined}
                            points={isAuth && operations ? currentMonthData.friendsSalesSum : 700}
                        />
                        <Divider />
                        <SalesItem
                            color="#B8D4FF"
                            isLoading={isLoading}
                            itemName="Пассивный доход"
                            itemDescription={isAuth ? 'От продаж в прошлых месяцах' : undefined}
                            hasTooltip={isAuth}
                            points={isAuth && operations ? currentMonthData.passiveIncome : 500}
                        />
                    </div>
                    {isAuth && (
                        <Link to={appRoutes.history} className={cn.historyLink}>
                            История операций<span className={cn.historyArrowIcon}>{ArrowIcon}</span>
                        </Link>
                    )}
                </div>
            </div>
            {!isAuth ? (
                <div className={cn.infoBlock}>
                    {isLoading ? (
                        <Skeleton />
                    ) : (
                        <>
                            <div className={cn.head}>
                                <h1>
                                    2 способа
                                    <br />
                                    заработать
                                </h1>
                                <div className={cn.infoRate}>1 Б = 1 рубль</div>
                            </div>
                            <div className={cn.waysToEarn}>
                                <WayToEarnList
                                    wayNumber={1}
                                    wayName="Продавай сам"
                                    itemsList={wayToEarnListOne}
                                />
                                <WayToEarnList
                                    wayNumber={2}
                                    wayName="Приведи друга"
                                    itemsList={wayToEarnListTwo}
                                />
                            </div>
                            <div className={cn.signUpCard}>
                                <div className={cn.signUpCardLeft}>
                                    <div className={cn.signUpCardHead}>
                                        <h2>Хочешь так же?</h2>
                                        <p>Регистрируйся и начни зарабатывать!</p>
                                    </div>
                                    <Button
                                        onClick={handleOpenSignUp}
                                        className={cn.signUpBtn}
                                        background="secondary"
                                    >
                                        Зарегистрироваться
                                    </Button>
                                </div>
                                <div className={cn.signUpCardRight}>
                                    <div className={cn.imgWrapper}>
                                        <img src="/images/main/phone0.svg" alt="" />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className={cn.authInfoBlock}>
                    <div className={cn.authWaysToEarn}>
                        <div className={cn.authWaysToEarnLeft}>
                            <h2>Как заработать в Другомании?</h2>
                            <LinkWithPrevLocation
                                to={`/${appRoutes.profile}/${appRoutes.loyaltyProgram}`}
                            >
                                <Button className={cn.authWaysToEarnBtn}>
                                    <div className={cn.coinsIcon}>
                                        <img src="/images/icons/coins_gradient.svg" alt="" />
                                    </div>
                                    <span className={cn.btnText}>2 способа зарабатывать</span>
                                    <span className={cn.arrowIcon}>{ArrowIcon}</span>
                                </Button>
                            </LinkWithPrevLocation>
                        </div>
                        <div className={cn.authWaysToEarnRight}>
                            <WayToEarnList itemsList={waysToEarnAuth} gap={8} />
                        </div>
                    </div>
                    <div className={cn.inviteFriendsBlock}>
                        <div className={cn.inviteFriendsBlockLeft}>
                            <h3>Пригласи друзей</h3>
                            <p>И получи больше бонусов</p>
                            <Button
                                className={cn.inviteFriendsBtn}
                                onClick={() => setIsInviteOpen(true)}
                            >
                                <span className={cn.linkIcon}>{LinkIcon}</span>Пригласить друга
                                зарабатывать
                            </Button>
                        </div>
                        <div className={cn.inviteFriendsBlockRight}>
                            <img src="/images/main/friends.svg" alt="" />
                        </div>
                    </div>
                    <div className={cn.faq}>
                        <h3>Частые вопросы</h3>
                        <div className={cn.faqList}>
                            {Object.entries(faq)
                                .slice(0, 2)
                                .map(([key, value], i, arr) => (
                                    <Fragment key={key}>
                                        <FAQCollapsible title={key} content={value} />
                                        {i < arr.length - 1 && <Divider />}
                                    </Fragment>
                                ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
